import { leadWaitingSince } from "./lead-waiting-clock.ts";

export { leadWaitingSince } from "./lead-waiting-clock.ts";

import { z } from "zod";
import type { SellerIdentity } from "../../../skills/leadcue-reply/scripts/lib/bison-contracts.ts";
import { isConditionalNoStep } from "../../../skills/leadcue-reply/scripts/lib/conditional-no.ts";
import { BOOKING_CONTEXT_KEY } from "../../../skills/leadcue-reply/scripts/lib/context-contracts.ts";
import { promiseTopic } from "../../../skills/leadcue-reply/scripts/lib/follow-up-promise.ts";
import {
  isSellerAddress,
  mergeSellerIdentities,
} from "../../../skills/leadcue-reply/scripts/lib/identity.ts";
import {
  type LeadLinkPlacement,
  leadLinkStepPlacement,
} from "../../../skills/leadcue-reply/scripts/lib/lead-scheduling-link.ts";
import { isMaterialStep } from "../../../skills/leadcue-reply/scripts/lib/materials.ts";
import {
  isShownOnlyKind,
  OUTSIDE_GOAL_LABELS,
  type OutsideGoalStepKind,
  outsideGoalStepKind,
} from "../../../skills/leadcue-reply/scripts/lib/outside-goal.ts";
import {
  type ReplyAskSchema,
  type ReplyStanceSchema,
  SCHEDULING_STEP_ASKS,
} from "../../../skills/leadcue-reply/scripts/lib/reply-classification.ts";
import {
  approvalIsCurrent,
  dependenciesSatisfied,
  goalCompletedAt,
  type HeldDisposition,
  type PlanStep,
  replySentForConversation,
  standingBookings,
  type WorkPlan,
} from "../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import { appCount, appDateTime, appDayMonth, appTime, appWeekdayTime } from "./app-dates.ts";
import { bookingStatusChoices } from "./booking-question.ts";
import { type GoalLifecycle, goalLifecycle, tracksCompletion } from "./goal-lifecycle.ts";
import {
  IN_FLIGHT_CHIP,
  type InFlightRun,
  type InFlightView,
  inFlightView,
  withoutOpenWork,
} from "./in-flight-view.ts";
import type { PhoneSummary } from "./phone-call-view.ts";

/*
 * Shared, pure owner-group projection; each conversation belongs to exactly one group. Priority:
 * booked, handled, other closed, your move, client's move, agreed incomplete, lead's move.
 * Owner tabs hold you/client/lead; other groups live under More. Handled stays searchable and
 * openable but never notifies. Designs: docs/2026-09-19-inbox-direction-a-design.md (3.1),
 * docs/2026-09-21-inbox-view-design.md, docs/2026-09-19-inbox-converting-first.md.
 */
/*
 * Visible inbox window; report truncation beside it. The repository orders by updated_at DESC,
 * id DESC so tied timestamps still have a stable order.
 */
export const INBOX_WINDOW_ROWS = 200;

/**
 * Bound calculated tab counts, not workspace size. Settled history is counted in SQL; above this
 * live-row ceiling, counts use the loaded window and label the approximation.
 * Raise only after measuring the full read in production at the proposed size:
 * scripts/operations/measure-inbox-counts.ts. Measurements and hardware differences:
 * docs/2026-10-13-inbox-large-workspace-design.md, section 3.
 */
export const INBOX_COUNT_CEILING = 1500;

export const INBOX_GROUPS = [
  "your_move",
  "agreed",
  "owed_by_client",
  "waiting_on_lead",
  "held",
  "closed",
  "handled",
] as const;
export type InboxGroup = (typeof INBOX_GROUPS)[number];

/* Distinguish the seller's client from the lead; naming rationale: docs/2026-09-21-inbox-view-design.md. */
export const inboxGroupLabel: Record<InboxGroup, string> = {
  your_move: "Your move",
  agreed: "Agreed, not completed",
  owed_by_client: "Waiting on client",
  waiting_on_lead: "Waiting on lead",
  held: "Held",
  closed: "Closed",
  handled: "Handled",
};

/*
 * One tab per owner of the next move, and one More for the rest (change 2). Agreed sits behind More
 * only because its rows already sort first in Your move and the opening summary leads with them as a
 * one-click filter; if the summary ever drops that bucket, Agreed comes back as a visible tab. `null`
 * is All.
 */
const OWNER_GROUPS = [
  "your_move",
  "owed_by_client",
  "waiting_on_lead",
] as const satisfies readonly InboxGroup[];

/**
 * The tab row for a view: which owners show, what More holds, and whether More holds the choice.
 *
 * An owner with nothing in it spends no tab: it is listed in More, so every group is still reachable
 * from exactly one place. Your move is home and always shows, and a chosen owner stays where it is
 * when its last row leaves, so nothing moves under the rep's pointer.
 */
export function ownerTabs(
  view: InboxView,
  counts: Record<InboxGroup, number>,
): { visible: InboxGroup[]; more: (InboxGroup | null)[]; moreSelected: boolean } {
  const chosen = view.focus === null ? view.group : null;
  const shows = (group: InboxGroup): boolean =>
    group === "your_move" || group === chosen || counts[group] > 0;
  const visible: InboxGroup[] = OWNER_GROUPS.filter(shows);
  const empty = OWNER_GROUPS.filter((group) => !shows(group));
  return {
    visible,
    more: [null, "agreed", ...empty, "held", "closed", "handled"],
    moreSelected: view.focus === null && (chosen === null || !visible.includes(chosen)),
  };
}

export const INBOX_SORTS = ["action", "newest"] as const;
export type InboxSort = (typeof INBOX_SORTS)[number];
export const inboxSortLabel: Record<InboxSort, string> = {
  action: "Whose move, agreed first",
  newest: "Newest first",
};

/** What a row carries into the projection: the plan and only the shape of its thread. */
export type InboxMessage = { direction: "inbound" | "outbound"; at: string; human?: boolean };
/**
 * Attribute seller mail by workspace identities plus outbound thread addresses, regardless of
 * Bison's folder: another seller mailbox can arrive as inbound. Apply on read to fix old imports too.
 */
export function sellerSideThread<M extends { direction: "inbound" | "outbound"; from: string }>(
  messages: readonly M[],
  list: SellerIdentity,
): M[] {
  const seller = mergeSellerIdentities(list, {
    addresses: messages.filter((message) => message.direction === "outbound").map((m) => m.from),
    domains: [],
  });
  return messages.map((message) =>
    message.direction === "inbound" && isSellerAddress(message.from, seller)
      ? { ...message, direction: "outbound" as const }
      : message,
  );
}

/** A `reply.classified` event, public labels only, as the inbox reads it. */
export type InboxClassification = { at: string; stance: string; asks: readonly string[] };
/**
 * A seller reply recorded against the row's current turn (R7 in
 * docs/2026-09-23-compose-view-shape.md): who wrote it, whether a rep reported it sent, and when it
 * left. Absent for a turn nobody has answered, and for every row a rollback build reads.
 */
export type AnsweredTurn = {
  author: "rep" | "pipeline" | "outside_app";
  reported: boolean;
  sentAt: string;
};

export type InboxEntry = {
  phone?: PhoneSummary | null | undefined;
  plan: WorkPlan;
  messages: readonly InboxMessage[];
  interested: boolean;
  /** A suppression stop recorded for this conversation: the lead is not written to again. */
  suppressed: boolean;
  /** Every classification of this conversation's replies, oldest first. */
  classifications: readonly InboxClassification[];
  answeredTurn?: AnsweredTurn | null;
  /** Set while the lead's newest message is one the app held during campaign setup. */
  lateReply?: { receivedAt: string } | null | undefined;
  /** The rep's newest answer that nothing has followed: the app may still be writing the draft. */
  inFlight?: InFlightRun | null | undefined;
  /**
   * When a new attempt asked for against the run shown was asked for, while it is still on its way
   * as far as the database can tell (`plan-repository.ts`, `REDRAFT_COLUMN`).
   */
  redraftSince?: string | null | undefined;
  /**
   * Commitment slots the newest generated draft waits on (`draft-slots.ts`): the row says how many
   * the rep has to confirm instead of "Draft ready". Absent for every row a rollback build reads.
   */
  slotsToConfirm?: number | undefined;
};

/*
 * Agreement is a scheduling ask: the lead asks for or confirms a time, a meeting's details, a
 * reschedule, attendees or a registration. It is the pipeline's own list, so the inbox cannot call
 * agreed a reply drafting marks `conditional_yes` and refuses to offer a meeting on. Engagement
 * alone is not agreement: since 2026-09-15 `interested_engaged` also holds a yes that waits on an
 * answer first (docs/2026-09-23-agreed-not-booked-investigation.md).
 */
const AGREED_ASK_SET = new Set<string>(SCHEDULING_STEP_ASKS);
/* Engagement orders your move (below); it never marks a lead as agreed. */
const ENGAGED_STANCES = ["interested_engaged"] as const satisfies readonly z.infer<
  typeof ReplyStanceSchema
>[];
// Events carry the labels as strings; the typed list above keeps them within the vocabulary.
const ENGAGED_STANCE_SET = new Set<string>(ENGAGED_STANCES);

/*
 * What the rep's attention is for: interest, questions, scheduling and objections. A soft decline
 * is an objection to answer, not housekeeping (Dawid, 19:23:50 CEST on 2026-09-19: "Draft a reply").
 */
const CONVERTING_STANCES = [
  "interested_engaged",
  "brushoff_soft_yes",
  "curiosity_no_intent",
  "timing_defer",
  "reverse_pitch_partnership",
  "not_interested_soft",
] as const satisfies readonly z.infer<typeof ReplyStanceSchema>[];
/** Replies that need no answer from the rep: a stop, a final no, or something that is not a lead. */
const HOUSEKEEPING_STANCES = [
  "opt_out_request",
  "not_interested_hard",
  "hostile_complaint",
  "legal_privacy_demand",
  "auto_reply_ooo",
  "auto_reply_other",
  "role_change_departure_auto_notice",
  "unrelated_correspondence",
  "not_a_lead_reply",
] as const satisfies readonly z.infer<typeof ReplyStanceSchema>[];
const CONVERTING_STANCE_SET = new Set<string>(CONVERTING_STANCES);

/** A classification the rep should see among the converting work, whatever else the thread holds. */
const isConverting = (entry: InboxClassification | undefined): boolean =>
  entry !== undefined &&
  autoReplyLabel(entry.stance) === null &&
  (CONVERTING_STANCE_SET.has(entry.stance) || entry.asks.some((ask) => AGREED_ASK_SET.has(ask)));

const housekeepingLabel: Record<(typeof HOUSEKEEPING_STANCES)[number], string> = {
  opt_out_request: "Asked to stop",
  not_interested_hard: "Declined",
  hostile_complaint: "Complaint",
  legal_privacy_demand: "Privacy request",
  auto_reply_ooo: "Out of office",
  auto_reply_other: "Auto-reply",
  role_change_departure_auto_notice: "Left the company",
  unrelated_correspondence: "Unrelated",
  not_a_lead_reply: "Not a lead",
};
const isHousekeeping = (stance: string): stance is keyof typeof housekeepingLabel =>
  stance in housekeepingLabel;

/*
 * The lead's ask in their own terms (change 3): the first chip on a row is what the lead wants, so
 * the rep reads the conversation before the chore. An ask names the request; a reply that named none
 * is described by its stance. `other` says nothing a chip could, so it is left out.
 */
const askLabel: Record<Exclude<z.infer<typeof ReplyAskSchema>, "other">, string> = {
  propose_times: "Asked for times",
  confirm_their_time: "Proposed a time",
  confirm_meeting_details: "Asked for details",
  reschedule: "Wants to reschedule",
  register_attendee: "Wants to register",
  add_attendees: "Adding people",
  send_materials: "Wants materials",
  answer_pricing: "Asked about price",
  confirm_scope: "Asked about scope",
  route_to_person: "Pointed to someone",
  answer_data_source: "Asked about data",
  answer_technical: "Technical question",
  provide_references: "Wants references",
  sign_nda: "Wants an NDA",
  route_to_procurement: "Sent to procurement",
  stop_until_date: "Not until later",
  remove_me: "Asked to stop",
};
const stanceLabel: Record<(typeof CONVERTING_STANCES)[number], string> = {
  interested_engaged: "Wants to talk",
  brushoff_soft_yes: "Soft yes",
  curiosity_no_intent: "Curious",
  timing_defer: "Not now",
  reverse_pitch_partnership: "Pitching back",
  not_interested_soft: "Soft no",
};
const hasLabel = <K extends string>(labels: Record<K, string>, key: string): key is K =>
  key in labels;

/** The lead's newest ask or stance as a chip, or null when the reply said nothing a chip could. */
function leadAskChip(latest: InboxClassification | undefined): InboxChip | null {
  if (latest === undefined) return null;
  if (autoReplyLabel(latest.stance) !== null) return null;
  const ask = latest.asks.find((candidate) => hasLabel(askLabel, candidate));
  if (ask !== undefined && hasLabel(askLabel, ask)) return { label: askLabel[ask], tone: "blue" };
  if (hasLabel(stanceLabel, latest.stance))
    return { label: stanceLabel[latest.stance], tone: "blue" };
  return null;
}

/**
 * The holds intake files a backfill-lane reply under: shown, never drafted. Not by themselves
 * "imported history", which is a conversation no run ever read (`isUntouchedHistory`): a reply of a
 * conversation the app drafted for can be filed under one too. Exported for the one other reader of
 * it, the SQL that counts settled history (`inbox-settled-history.ts`), so the two cannot name
 * different dispositions.
 */
export const HISTORY_HOLDS: ReadonlySet<WorkPlan["disposition"]> = new Set([
  "backfill_not_drafted",
  "back_drafting_disabled",
]);

/**
 * Why a conversation is folded into Handled, or null when it belongs with the rep's work. Pure, and
 * shared by the inbox and the notifier so a folded conversation never notifies.
 *
 * A converting latest reply always wins, so a lead who writes back interested lifts the thread out.
 * Open answering work keeps it out too: an unanswered question, a draft, an owed answer, or an
 * effect whose result needs checking. A reply newer than the latest classification is shown until
 * it is classified, except on held history, which is never drafted.
 *
 * Imported history has a twin of this rule in SQL: `inbox-settled-history.ts` counts the rows this
 * folds as "History · not drafted" without calling it, held to this function by
 * `tests/inbox-settled-history-postgres.test.ts`. A new way out of Handled for imported history
 * must be named there too, as a clause with a planted plan, or the lead is counted and never shown.
 */
export function handledReason(
  entry: Pick<InboxEntry, "plan" | "messages" | "suppressed" | "classifications" | "answeredTurn">,
): string | null {
  const { plan, classifications, messages } = entry;
  const latest = classifications.at(-1);
  const newestInbound = lastOf(messages, "inbound");
  if (latest !== undefined && newestInbound !== null && ms(newestInbound) > ms(latest.at))
    return null;
  // A reply outside the campaign's goal is the rep's to handle, whatever its stance folds to.
  if (openOutsideGoalKind(plan) !== null) return null;
  // So is a lead's own booking link: the rep books on it (`lead-scheduling-link.ts`).
  if (openLeadLinkStep(plan) !== null) return null;
  // And a no with a condition: the rep decides whether it holds (`conditional-no.ts`). Their No
  // completes the step, and the row then folds as "Declined" below.
  if (openConditionalNoStep(plan) !== null) return null;
  if (
    plan.followUpPromises.some((promise) => promise.state !== "kept") ||
    plan.steps.some((step) => ["executing", "uncertain"].includes(step.state))
  )
    return null;
  if (plan.turnTreatment === "close_decline") return "Declined";
  if (
    plan.turnTreatment === "answer_then_close" &&
    (reportedAt(plan) !== null || entry.answeredTurn?.reported)
  )
    return "Answered · declined";
  if (plan.turnTreatment === "answer_then_close") return null;
  /*
   * A thread whose only open work is a stop someone else carries out has nothing for the rep to do,
   * so it is a fact to record rather than a queue entry. It folds even on a converting stance, which
   * is how three of the pilot's 55 plans came to sit at the head of Your move behind a gold "To do"
   * on 2026-09-21: a final-refusal run labelled `not_interested_soft`. The disagreement between that
   * label and the pipeline's own intent is reported in docs/2026-09-21-inbox-morning-view.md, not
   * tuned here.
   *
   * It is the fallback label, never the first: a reason that already says why the row folded — a
   * decline, an opt-out, held history — tells the rep more than the mechanism does.
   */
  const recordedStop = onlyRecordedStopsOpen(plan) ? RECORDED_STOP_LABEL : null;
  if (isConverting(latest)) return recordedStop;
  const answering = plan.steps.some(
    (step) =>
      ["executing", "uncertain"].includes(step.state) ||
      (["question", "reply"].includes(step.input.kind) && !FINISHED.has(step.state)),
  );
  if (answering || plan.followUpPromises.some((promise) => promise.state !== "kept")) return null;
  if (isUntouchedHistory(entry)) return "History · not drafted";
  // Automatic notices leave the lead's answer outstanding; they are visible in Waiting on lead.
  if (latest && autoReplyLabel(latest.stance) !== null) return null;
  if (entry.suppressed) return "Opted out";
  if (latest === undefined) return recordedStop;
  return isHousekeeping(latest.stance) ? housekeepingLabel[latest.stance] : recordedStop;
}

/**
 * A stop or a final refusal as the row's first chip, in the word Handled uses for it, or null. A no
 * usually folds into Handled and says so there; one that an owed answer or an unsettled action
 * holds in the rep's queue named only the work, and nothing on the row said the lead had refused.
 * Read from the closed offer, so a no the lead has since written past is not repeated. Not asked
 * for where the row already has two chips to draw (`leadingChip`'s `room`).
 */
function refusalChip(
  latest: InboxClassification | undefined,
  closedBy: BookingOffer["closedBy"],
): InboxChip | null {
  if (latest === undefined || (closedBy !== "stop" && closedBy !== "refusal")) return null;
  if (!STOP_STANCES.has(latest.stance) && !REFUSAL_STANCES.has(latest.stance)) return null;
  return isHousekeeping(latest.stance)
    ? { label: housekeepingLabel[latest.stance], tone: "blue" }
    : null;
}

function autoReplyLabel(stance: string): "Out of office" | "Auto-reply" | null {
  if (stance === "auto_reply_ooo") return "Out of office";
  if (stance === "auto_reply_other") return "Auto-reply";
  return null;
}

/**
 * When the lead agreed: the first classification carrying a scheduling ask. The clock
 * runs from there; a booking takes the row out of the group, and an undone booking puts it back
 * with the same clock.
 */
export function agreedAt(classifications: readonly InboxClassification[]): string | null {
  return (
    classifications.find(
      (entry) =>
        autoReplyLabel(entry.stance) === null && entry.asks.some((ask) => AGREED_ASK_SET.has(ask)),
    )?.at ?? null
  );
}

/* A latest final refusal invalidates earlier agreement; a recorded stop survives later replies. */
const STOP_STANCES = new Set<string>(["opt_out_request"]);
const REFUSAL_STANCES = new Set<string>([
  "not_interested_hard",
  "hostile_complaint",
  "legal_privacy_demand",
]);

export type BookingOffer = {
  /** When the lead first agreed, or null when they never did. */
  agreedAt: string | null;
  /** Why Mark booked is closed, or null while it is open. */
  closedBy: "booking" | "stop" | "refusal" | null;
};

/**
 * Shared UI/API guard: standing bookings (including history after configuration refresh), recorded
 * stops and the latest stop/refusal close Mark booked. A newer lead reply clears an old refusal,
 * even when drafting-off prevented reclassification; it never clears a stop. Rationale:
 * docs/2026-10-08-your-move-drafting-off-investigation.md, section 4.
 */
export function bookingOffer(
  entry: Pick<InboxEntry, "plan" | "suppressed" | "classifications" | "messages">,
): BookingOffer {
  const agreed = agreedAt(entry.classifications);
  if (entry.plan.goalCompletion || standingBookings(entry.plan).length > 0)
    return { agreedAt: agreed, closedBy: "booking" };
  if (entry.suppressed) return { agreedAt: agreed, closedBy: "stop" };
  const latest = entry.classifications.at(-1);
  if (latest && (STOP_STANCES.has(latest.stance) || latest.asks.includes("remove_me")))
    return { agreedAt: agreed, closedBy: "stop" };
  const newestInbound = lastOf(entry.messages, "inbound");
  const current = latest && (newestInbound === null || ms(newestInbound) <= ms(latest.at));
  if (latest && current && REFUSAL_STANCES.has(latest.stance))
    return { agreedAt: agreed, closedBy: "refusal" };
  return { agreedAt: agreed, closedBy: null };
}

type StoredEventView = { kind: string; occurredAt: string; detail: object };

/** What the detail panel can rebuild of an inbox entry from the conversation's stored events. */
function fromEvents(events: readonly StoredEventView[]) {
  const labels = z.object({ stance: z.string(), asks: z.array(z.string()) });
  return {
    suppressed: events.some((event) => event.kind === "suppression.recorded"),
    classifications: events.flatMap((event) => {
      const detail = event.kind === "reply.classified" ? labels.safeParse(event.detail) : null;
      return detail?.success ? [{ at: event.occurredAt, ...detail.data }] : [];
    }),
  };
}

/** The same decision from a conversation's thread and stored events, as the detail panel has them. */
export function bookingOfferFromEvents(
  plan: WorkPlan,
  messages: readonly InboxMessage[],
  events: readonly StoredEventView[],
): BookingOffer {
  return bookingOffer({ plan, messages, ...fromEvents(events) });
}

/*
 * The Handled reasons that mean the lead said no. Such a conversation is shown with its full thread
 * and asks nothing of the rep: stopping, unsubscribing or blocking the lead is Email Bison's job
 * (AGENTS.md Boundaries; Dawid, 11:36 CEST on 2026-09-28).
 */
const NO_REASONS = new Set([
  "Declined",
  "Answered · declined",
  "Asked to stop",
  "Opted out",
  "Complaint",
  "Privacy request",
]);

/**
 * Why a conversation is a settled no, in the Handled label's own words, or null when it is not one.
 * A lead who opted out earlier and has written back without converting is a no too, even though the
 * inbox shows that row under Closed rather than Handled.
 */
export function declinedStatus(
  entry: Pick<InboxEntry, "plan" | "messages" | "suppressed" | "classifications" | "answeredTurn">,
): string | null {
  const reason = handledReason(entry);
  // `RECORDED_STOP_LABEL` is declared further down, so it is read here rather than in the set.
  if (reason !== null)
    return NO_REASONS.has(reason) || reason === RECORDED_STOP_LABEL ? reason : null;
  if (entry.suppressed && !isConverting(entry.classifications.at(-1))) return "Opted out";
  return null;
}

/**
 * Whether the lead's last word is a no, whatever work is still open on the conversation.
 *
 * `declinedStatus` cannot answer that: `handledReason` keeps a row in the rep's queue on any unkept
 * promise or unsettled action before it reads the decline, which is right for where the row sits and
 * wrong for anything that would rank it. So this reads the refusal itself: the settled no, a stop or
 * a refusal that closes Mark booked, and a turn the plan closed as a decline. As in `bookingOffer`,
 * a decline stops describing the lead once they write again, and a stop does not.
 */
export function saidNo(
  entry: Pick<InboxEntry, "plan" | "messages" | "suppressed" | "classifications" | "answeredTurn">,
): boolean {
  if (declinedStatus(entry) !== null) return true;
  const { closedBy } = bookingOffer(entry);
  if (closedBy === "stop" || closedBy === "refusal") return true;
  if (entry.plan.turnTreatment !== "close_decline") return false;
  const latest = entry.classifications.at(-1);
  const newestInbound = lastOf(entry.messages, "inbound");
  return latest === undefined || newestInbound === null || ms(newestInbound) <= ms(latest.at);
}

/** The same judgment from the detail panel's own records: its plan, thread and stored events. */
export function declinedStatusFromEvents(
  plan: WorkPlan,
  messages: readonly InboxMessage[],
  events: readonly StoredEventView[],
): string | null {
  return declinedStatus({ plan, messages, ...fromEvents(events) });
}

/**
 * Whether the thread's only yes is the time a reply outside the campaign's goal named. Such a reply
 * is shown to the rep and never worked (AGENTS.md, Boundaries): an existing client who asks for a
 * call about their account on Tuesday has agreed to nothing the campaign offered.
 *
 * Read from the plan's own step for that reply (`outside-goal.ts`), open or settled: a plan's steps
 * are its current turn's, so the row does not move under Agreed once the rep has answered it. The
 * label that named the time is that reply's own, the newest one that is not an automatic reply. A
 * yes from an earlier reply, to the campaign's offer, is the first label with a time and so still
 * stands. A reply the host only shows (`isShownOnlyKind`) was never read: no label is its own.
 */
function agreedOutsideGoal(entry: Pick<InboxEntry, "plan" | "classifications">): boolean {
  const read = entry.plan.steps.some((step) => {
    const kind = outsideGoalStepKind(step);
    return kind !== null && !isShownOnlyKind(kind);
  });
  if (!read) return false;
  const own = entry.classifications.findLast((label) => autoReplyLabel(label.stance) === null);
  return own !== undefined && agreedAt(entry.classifications) === own.at;
}

/**
 * State a commitment only when it is not untouched imported history, a later no, or a time named
 * outside the campaign goal. Drafted conversations retain their yes across a later history hold:
 * docs/2026-10-14-history-means-untouched.md.
 * Share this rule across row, summary, detail and two-track membership. If a yes cannot be stated,
 * it must not choose the tab either: place the conversation as if it had no yes.
 * This does not govern Mark booked; bookingOffer can allow a booking without a stated yes.
 * Claim rationale: docs/2026-10-13-inbox-row-claims.md and
 * docs/2026-10-13-inbox-history-out-of-agreed.md.
 */
function claimsNoCommitment(
  entry: Pick<InboxEntry, "plan" | "messages" | "suppressed" | "classifications" | "answeredTurn">,
): boolean {
  return isUntouchedHistory(entry) || saidNo(entry) || agreedOutsideGoal(entry);
}

/**
 * The offer as the open conversation states it: `bookingOffer`'s own decision on Mark booked, with
 * the lead's yes left out where the inbox row would not state it either (`claimsNoCommitment`).
 * The card "Agreed, not booked", its sentence and the Work line all read `agreedAt`, so they follow
 * the row; Mark booked stays where an open offer without a yes has it, under the other outcomes.
 */
export function statedOfferFromEvents(
  plan: WorkPlan,
  messages: readonly InboxMessage[],
  events: readonly StoredEventView[],
): BookingOffer {
  const offer = bookingOfferFromEvents(plan, messages, events);
  return claimsNoCommitment({ plan, messages, ...fromEvents(events) })
    ? { ...offer, agreedAt: null }
    : offer;
}

/*
 * Whether a completion can be recorded for this plan's goal, which is what lets a row hold the
 * "committed, not completed" state at all: with no receipt nothing could ever take it out again.
 * This was `bookable`, true for two goal kinds while five exist; the wording and the threshold now
 * come from `goal-lifecycle.ts`, so a contribution campaign is never asked about a booking.
 */
const completionRecordable = (plan: WorkPlan): boolean => tracksCompletion(plan.goalKind);

/*
 * A chip names the kind of work or the reason, in the words the work panel uses. `tone` is the
 * design system's semantic colour, never decoration: blue is what the lead said, gold is the rep's
 * own next action, amber is the system holding back, slate is neutral status, green is done, red is
 * a stop.
 */
type InboxChipTone = "gold" | "amber" | "slate" | "green" | "red" | "blue";
/**
 * holdsRow keeps visible what we still owe a lead who refused. writing describes an active
 * re-draft and takes priority over both ordinary chips and that queue reason.
 */
export type InboxChip = {
  label: string;
  tone: InboxChipTone;
  holdsRow?: true;
  writing?: true;
};

/*
 * The one clock a row shows (change 7), with its word visible, so the rep never has to guess what a
 * number measures. `at` is an instant, or for the promise words a calendar date (`YYYY-MM-DD`).
 * `warn` is the amber of a lead waiting over a working day or a commitment past its reminder;
 * `alert` is an overdue promise.
 */
export type InboxClock = {
  /**
   * `Since` is when drafting started after an answer, `Asked` when a new draft was asked for: the two
   * clocks shown as a time of day, never as an age.
   */
  label:
    | "Waiting"
    | "Agreed"
    | "Due"
    | "Due today"
    | "Overdue"
    | "Sent"
    | "Last"
    | "History"
    | "Since"
    | "Asked";
  at: string;
  tone: "plain" | "warn" | "alert";
};

export type InboxPosition = {
  group: InboxGroup;
  /** The lead's ask first, then the rep's debt, then state (change 3). */
  chips: InboxChip[];
  clock: InboxClock | null;
  /** An owed answer whose follow-up date is today or past: the rep's move, whoever checks. */
  promiseDue: "today" | "overdue" | null;
  /** Set while the app is writing the draft, or stopped before one: the row is not actionable. */
  inFlight?: InFlightView;
  /**
   * For your move, the oldest inbound after our last outbound: how long the lead has waited. For
   * agreed, when the lead agreed. For waiting on the lead, when we last wrote or reported sending.
   * Null when the thread has neither, and for a row under Agreed that states no commitment.
   */
  since: string | null;
  /**
   * A commitment that still stands with no completion recorded, and that the row may state: never
   * imported history, never a lead whose last word is a no (`claimsNoCommitment`). Such a row sorts
   * first within your move and within agreed, in either group, because it is the one the lead is
   * waiting on, and it is what the summary counts as agreed.
   */
  committed: boolean;
  /** Committed for at least the goal's reminder threshold with no completion recorded. */
  reminder: boolean;
};

/*
 * Held conversations stay in your move with a label naming why no draft exists (Dawid, decision 1),
 * except a drafting-off hold, which has the Held group of its own (`whoseMove`).
 * `sender_only_cc` and `ignored_not_qualified` are not the system holding back a reply to us, and
 * `answered_by_seller` was answered in Bison, so none of those three is here.
 */
export const heldLabel: Record<
  Exclude<HeldDisposition, "sender_only_cc" | "ignored_not_qualified" | "answered_by_seller">,
  string
> = {
  drafting_off: "Held · drafting off",
  back_drafting_disabled: "Held · imported-history drafting unavailable",
  backfill_not_drafted: "Held · older than the backfill window",
  qualification_pending: "Held · awaiting qualification",
  qualification_error: "Held · qualification error",
  meeting_already_confirmed: "Held · meeting already booked",
  continued_not_drafted: "Held · wrote again outside the campaign",
};
const isHeld = (disposition: WorkPlan["disposition"]): disposition is keyof typeof heldLabel =>
  disposition in heldLabel;

/**
 * Where drafting was switched off for a held reply, so the rep knows which switch to turn back on.
 * Null for a hold recorded before the switches existed.
 */
export function draftingOffPhrase(level: WorkPlan["draftingHeldBy"]): string {
  return level === null ? "Drafting off" : `Drafting off for this ${level}`;
}

/**
 * What a reply held because drafting is off is, and what becomes of it once drafting is switched
 * back on (`releaseHeldDrafting`, intake-repository.ts). A reply that came in while its campaign was
 * processed is drafted then, however old (Dawid, decision 23b50668). Two are not: one that came in
 * before the workspace's latest re-onboarding, since an offboarding came between (D2), and imported
 * history held this way, which needs back-drafting on, once it is past the drafting limit. That
 * limit is the backfill window (`maxDraftAgeDays`, 7 days unless configured), counted from when the
 * lead wrote, at the claim; the sheet of a reply held for it already calls it "the drafting limit".
 */
export function draftingOffNote(level: WorkPlan["draftingHeldBy"]): string {
  const where = level === null ? "" : ` for this ${level}`;
  return `Drafting was off${where} when this reply came in, so nothing was drafted. It is drafted once drafting is switched back on, unless it came in before this workspace was last offboarded, or with imported history older than the drafting limit. To answer it sooner, reply in Bison.`;
}

/** The chip for a held conversation; a drafting hold names the level that holds it. */
export function heldChipLabel(
  plan: Pick<WorkPlan, "draftingHeldBy"> & { disposition: keyof typeof heldLabel },
): string {
  return plan.disposition === "drafting_off"
    ? `Held · ${draftingOffPhrase(plan.draftingHeldBy).toLowerCase()}`
    : heldLabel[plan.disposition];
}

const FINISHED = new Set<PlanStep["state"]>(["completed", "reported", "skipped"]);

/*
 * Legacy stop actions remain stored for rollback, but Bison owns them (AGENTS.md Boundaries).
 * visibleSteps must exclude them from display, counts, chips and notifications;
 * bisonStopCommandRefusal must refuse API execution. Match the action-<type>- id prefix emitted by
 * run-projection.ts: stop_contact (manual), suppress (unsubscribe/block connector or manual),
 * and review_writer_on_stopped_thread (manual). Do not rewrite persisted plans to hide them.
 */
const BISON_STOP_PREFIXES = [
  "action-stop_contact-",
  "action-suppress-",
  "action-review_writer_on_stopped_thread-",
] as const;

export function isBisonStopStep(step: PlanStep): boolean {
  if (step.input.kind === "connector")
    return step.input.action.type === "unsubscribe" || step.input.action.type === "block";
  return (
    step.input.kind === "manual" && BISON_STOP_PREFIXES.some((prefix) => step.id.startsWith(prefix))
  );
}

/** The plan's steps a rep may see or act on: everything except a stop that belongs to Bison. */
export const visibleSteps = (plan: Pick<WorkPlan, "steps">): PlanStep[] =>
  plan.steps.filter((step) => !isBisonStopStep(step));

/**
 * Why the commands route refuses to act on a step, or null when it may. A stop that belongs to Bison
 * is refused whatever the action, so a hidden step cannot be approved, queued or completed through
 * the API either: nothing in the app can queue an unsubscribe or a block.
 */
export function bisonStopCommandRefusal(
  plan: Pick<WorkPlan, "steps">,
  command: { action: string; stepId?: string | undefined },
): "bison_owns_stops" | null {
  const step = plan.steps.find((candidate) => candidate.id === command.stepId);
  return step && isBisonStopStep(step) ? "bison_owns_stops" : null;
}

/*
 * Operator handoffs for needs_review/stage_failed use handoff-developer-review-… ids.
 * Render amber system-held work, not the rep's gold next action; handoff-artifact.test.ts
 * witnesses the id through the real projection.
 */
const DEVELOPER_REVIEW_STEP_PREFIX = "handoff-developer-review-";

export const isDeveloperReviewStep = (step: PlanStep): boolean =>
  step.input.kind === "handoff" && step.id.startsWith(DEVELOPER_REVIEW_STEP_PREFIX);

/** The label for a row whose only remaining step is a stop that belongs to Bison. */
export const RECORDED_STOP_LABEL = "Stop recorded · Bison";

/**
 * Whether the plan has open steps and every one is a stop that belongs to Bison: an older plan's
 * record of a no. A plan with nothing open is not this case: it is decided by the rules below. An
 * owed answer still holds the row, because a promise the rep made outlives the thread's own ending.
 */
function onlyRecordedStopsOpen(plan: WorkPlan): boolean {
  if (plan.followUpPromises.some((promise) => promise.state !== "kept")) return false;
  const open = plan.steps.filter((step) => !FINISHED.has(step.state));
  return open.length > 0 && open.every(isBisonStopStep);
}
const ms = (at: string): number => Date.parse(at);

function lastOf(messages: readonly InboxMessage[], direction: InboxMessage["direction"]) {
  return messages.findLast((message) => message.direction === direction)?.at ?? null;
}

/**
 * The newest booking still standing, current or kept only in history after a config change. A
 * withdrawn Mark booked no longer counts.
 */
function booking(plan: WorkPlan): string | null {
  const latest = standingBookings(plan).at(-1);
  return latest ? goalCompletedAt(latest) : null;
}

/** Reported sending of the conversation as it now stands; a newer inbound lifts it. */
function reportedAt(plan: WorkPlan): string | null {
  if (!replySentForConversation(plan, plan.conversationRevision)) return null;
  return (
    plan.sentRevisions.findLast((entry) => entry.conversationRevision === plan.conversationRevision)
      ?.reportedAt ?? null
  );
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** Steps the rep can act on now: pending or awaiting re-approval, with nothing ahead of them. */
const actionableSteps = (plan: WorkPlan): PlanStep[] =>
  visibleSteps(plan).filter(
    (step) =>
      (step.state === "pending" || step.state === "invalidated") &&
      dependenciesSatisfied(plan, step),
  );

/**
 * The questions waiting on the rep's answer. A question already covered by a promise to check is
 * not one: the rep chose Need to check and the promise carries it from there.
 */
export function openQuestions(plan: WorkPlan) {
  const promisedSpeechActs = new Set(
    plan.followUpPromises
      .filter((promise) => promise.state !== "kept")
      .map((promise) => promise.speechActId),
  );
  return actionableSteps(plan)
    .flatMap((step) => (step.input.kind === "question" ? step.input.questions : []))
    .filter((question) =>
      question.speechActId === null ? true : !promisedSpeechActs.has(question.speechActId),
    );
}

/**
 * The kind of the open step a reply outside the campaign's goal left for the rep (`outside-goal.ts`),
 * or null. Such a row is in Your move whatever its stance, and leads with the kind.
 */
export function openOutsideGoalKind(plan: Pick<WorkPlan, "steps">): OutsideGoalStepKind | null {
  const step = openOutsideGoalStep(plan);
  return step ? outsideGoalStepKind(step) : null;
}

/** That open step itself, for the page's sheet: its id to act on and its source, the writer's words. */
export function openOutsideGoalStep(plan: Pick<WorkPlan, "steps">): PlanStep | null {
  return (
    plan.steps.find(
      (step) =>
        outsideGoalStepKind(step) !== null && ["pending", "invalidated"].includes(step.state),
    ) ?? null
  );
}

/**
 * The open step a lead's own scheduling link left for the rep (`lead-scheduling-link.ts`), or null.
 * Such a row is in Your move whatever its stance, and leads with "Their booking link". Only a pending
 * step is open: "Mark booked" invalidates every step, and a booked task has left the rep's queue.
 */
export function openLeadLinkStep(plan: Pick<WorkPlan, "steps">): PlanStep | null {
  return (
    plan.steps.find((step) => leadLinkStepPlacement(step) !== null && step.state === "pending") ??
    null
  );
}

/**
 * The open card a no with a named condition left for the rep (`conditional-no.ts`), or null. Such a
 * row is in Your move although its turn is a closed decline, and leads with "No, unless". Only a
 * pending step is open: the rep's No reports it, and a later turn closes it.
 */
export function openConditionalNoStep(plan: Pick<WorkPlan, "steps">): PlanStep | null {
  return plan.steps.find((step) => isConditionalNoStep(step) && step.state === "pending") ?? null;
}

/** Whether the plan's current turn has an open card for material (`materials.ts`). */
export function hasOpenMaterialStep(plan: Pick<WorkPlan, "steps">): boolean {
  return plan.steps.some((step) => isMaterialStep(step) && step.state === "pending");
}

/** Where the open lead-link task's link was found, for the page's sheet: body or signature. */
export function openLeadLinkPlacement(plan: Pick<WorkPlan, "steps">): LeadLinkPlacement | null {
  const step = openLeadLinkStep(plan);
  return step ? leadLinkStepPlacement(step) : null;
}

/** The rep's open work, as chips. Empty when nothing is actionable now. */
export function openWork(plan: WorkPlan): InboxChip[] {
  const open = actionableSteps(plan);
  const questions = openQuestions(plan);
  // A reply behind an unanswered question is shown as the question, the thing to do first.
  const blockedReply = plan.steps.some(
    (step) =>
      step.input.kind === "reply" && step.state === "pending" && !dependenciesSatisfied(plan, step),
  );
  const chips: InboxChip[] = [];
  const [only] = questions;
  if (questions.length === 1 && only?.answerMode === "yes_no_check")
    chips.push({ label: `Needs answer: ${promiseTopic(only.prompt)}`, tone: "gold" });
  // The booking question is asked in the goal's own words, as its card asks it (change 6).
  else if (questions.length === 1 && only?.key === BOOKING_CONTEXT_KEY)
    chips.push({ label: bookingStatusChoices(plan.goalKind).question, tone: "gold" });
  else if (questions.length > 0)
    chips.push({ label: plural(questions.length, "question", "questions"), tone: "gold" });
  const reply = open.find((step) => step.input.kind === "reply");
  if (reply && !blockedReply)
    chips.push({
      label: reply.state === "invalidated" ? "Re-approve draft" : "Draft ready",
      tone: "gold",
    });
  if (open.some((step) => step.input.kind === "connector"))
    chips.push({ label: "Approve action", tone: "gold" });
  // A chore says what it is: "To do" told the rep nothing they could act on from the list.
  const chores = open.filter((step) =>
    ["manual", "handoff", "handoff_artifact"].includes(step.input.kind),
  );
  const [chore] = chores;
  if (chore)
    chips.push({
      label:
        outsideGoalStepKind(chore) !== null
          ? "Handle yourself"
          : leadLinkStepPlacement(chore) !== null
            ? "Book on their link"
            : isConditionalNoStep(chore)
              ? "Does it hold?"
              : chores.length > 1
                ? `${chore.title} +${chores.length - 1}`
                : chore.title,
      // A stop for the operator is the system holding back, not a task the rep can do from here.
      tone: chores.every(isDeveloperReviewStep) ? "amber" : "gold",
    });
  if (plan.steps.some((step) => step.state === "uncertain"))
    chips.push({ label: "Check result", tone: "red" });
  const approved = plan.steps.find(
    (step) =>
      step.input.kind === "reply" && step.state === "approved" && approvalIsCurrent(plan, step),
  );
  if (approved) chips.push({ label: "Send in Bison", tone: "gold" });
  return chips;
}

type BasePosition = Omit<InboxPosition, "reminder" | "committed">;

const capitalized = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);
/**
 * The UTC calendar day, the same reading a promise's follow-up date was authorized against. It is
 * the only reading of the clock that moves a conversation between groups, so the saved inbox
 * status is kept for exactly this day (`inbox-saved-status.ts`).
 */
export const calendarDay = (now: Date): string => now.toISOString().slice(0, 10);
/**
 * Untouched history is a HISTORY_HOLDS disposition with no reply.classified label from a run.
 * Its age is an event date, not a waiting clock. Disposition alone is insufficient: intake can
 * re-file a drafted conversation as history, including offboarded/disabled and older rows, while
 * its labels survive. Preserve that distinction to retain agreed clocks/counts/Do first.
 * Keep this predicate aligned with inbox-settled-history.ts SQL clauses history/no_classification.
 * Background: docs/2026-10-14-history-means-untouched.md, docs/2026-10-14-late-reply-drafts.md.
 */
export const isUntouchedHistory = (entry: Pick<InboxEntry, "plan" | "classifications">): boolean =>
  HISTORY_HOLDS.has(entry.plan.disposition) && entry.classifications.length === 0;

/** A row that reads as a date: its last message, as history where the conversation is history. */
const lastMessageClock = (
  entry: Pick<InboxEntry, "plan" | "messages" | "classifications">,
): InboxClock | null => {
  const newest = entry.messages.at(-1);
  return newest
    ? { label: isUntouchedHistory(entry) ? "History" : "Last", at: newest.at, tone: "plain" }
    : null;
};

/**
 * When the seller's answer went out. Imported history reads as the date of its last message
 * instead, as every other history row does: "Sent 36 d" counted the days on a conversation the app
 * never touched, in the tab where a history row with an old yes now sits reading "History 6 Sep".
 * The last message and not the answer's recorded time, because the clock's tooltip names it
 * ("Imported history; last message on ...") and a recorded answer can carry another instant.
 */
const answerClock = (
  entry: Pick<InboxEntry, "plan" | "messages" | "classifications">,
  at: string | null,
): InboxClock | null => {
  if (at === null) return null;
  return isUntouchedHistory(entry) ? lastMessageClock(entry) : { label: "Sent", at, tone: "plain" };
};

/**
 * The row's first chip wherever the next move is still open (change 3): the lead's ask in their own
 * terms. A reply outside the campaign's goal leads with what it is instead, because it asks nothing
 * of the sale; so do a lead's own booking link and a no with a condition, which are the rep's task.
 */
function leadingChip(
  entry: InboxEntry,
  closedBy: BookingOffer["closedBy"],
  room: boolean,
): InboxChip | null {
  const outsideGoal = openOutsideGoalKind(entry.plan);
  if (outsideGoal !== null) return { label: OUTSIDE_GOAL_LABELS[outsideGoal], tone: "blue" };
  if (openLeadLinkStep(entry.plan) !== null) return { label: "Their booking link", tone: "blue" };
  if (openConditionalNoStep(entry.plan) !== null) return { label: "No, unless", tone: "blue" };
  const latest = entry.classifications.at(-1);
  return leadAskChip(latest) ?? (room ? refusalChip(latest, closedBy) : null);
}

/**
 * A conversation a new attempt is being written for (w563, Steward brief `6c98774c`): where it is the
 * rep's move, the row says the app is drafting again, from when it was asked, as the open
 * conversation does (`web/draft-again.tsx`, `useRedraft`). The chips of the work the attempt replaces
 * go; any other stays. Before, the row said "Draft ready" for a draft the conversation no longer shows.
 */
function redrafted(entry: InboxEntry, moved: BasePosition): BasePosition {
  const since = entry.redraftSince ?? null;
  if (since === null || moved.group !== "your_move") return moved;
  const replaced = new Set(openWork(entry.plan).map((chip) => chip.label));
  for (const chip of openWork(withoutOpenWork(entry.plan))) replaced.delete(chip.label);
  const writing: InboxChip = { ...IN_FLIGHT_CHIP.redrafting, writing: true };
  return {
    ...moved,
    chips: [writing, ...moved.chips.filter((chip) => !replaced.has(chip.label))],
    // An answer owed today or overdue keeps its louder clock; the chip says the rest.
    clock: moved.promiseDue ? moved.clock : { label: "Asked", at: since, tone: "plain" },
    inFlight: { kind: "redrafting", since },
  };
}

/**
 * A draft whose commitment slots wait on the rep is not ready to approve, so its chip says how many
 * there are to confirm (`draft-slots.ts`). Only the open draft's chip changes; a row whose draft is
 * being drafted again has none to change.
 */
function toConfirm(chips: readonly InboxChip[], slots: number): InboxChip[] {
  if (slots <= 0) return [...chips];
  return chips.map((chip) =>
    chip.label === "Draft ready" || chip.label === "Re-approve draft"
      ? { ...chip, label: `${slots} to confirm` }
      : chip,
  );
}

/**
 * Where a conversation sits, and why. The clock is an explicit input: it decides the reminder, a
 * promise's due date and the waiting tone, never anything else.
 */
export function inboxPosition(entry: InboxEntry, now: Date): InboxPosition {
  const moved = redrafted(entry, whoseMove(entry, now));
  const settled = moved.group === "closed" || moved.group === "handled";
  const offer = bookingOffer(entry);
  // The refusal word leads only a row with room for it: it never pushes open work behind "+1".
  const ask = settled ? null : leadingChip(entry, offer.closedBy, moved.chips.length < 2);
  const optedOut: InboxChip[] =
    entry.suppressed && !settled ? [{ label: "Opted out earlier", tone: "red" }] : [];
  const base: BasePosition = {
    ...moved,
    chips: [
      ...(ask ? [ask] : []),
      ...toConfirm(moved.chips, entry.slotsToConfirm ?? 0),
      ...optedOut,
    ],
  };
  /*
   * A yes that stands: the lead agreed, a completion can be recorded and none was, and no stop or
   * refusal has closed the offer since (`bookingOffer`). The app has not read a held reply, so an
   * earlier agreement does not move it to Agreed. Nor does a yes the row does not state
   * (`claimsNoCommitment`): that row sits where `whoseMove` put it.
   */
  const since = offer.agreedAt;
  const standing = since !== null && completionRecordable(entry.plan) && offer.closedBy === null;
  if (!standing || settled || base.group === "held" || claimsNoCommitment(entry))
    return { ...base, committed: false, reminder: false };
  const lifecycle = goalLifecycle(entry.plan.goalKind);
  const elapsed = now.getTime() - ms(since);
  const overdue = lifecycle.reminderAfterMs !== null && elapsed >= lifecycle.reminderAfterMs;
  /* Let the summary and Agreed clock state commitment; another chip would repeat it. */
  const agreedClock: InboxClock = { label: "Agreed", at: since, tone: overdue ? "warn" : "plain" };
  /*
   * While the rep's move is open the row stays in your move — but it keeps its commitment clock and
   * sorts first there, which is the whole point: the lead who said yes and is still waiting on us is
   * the most perishable thing in the inbox. Only an overdue promise outranks it, as the louder clock.
   */
  if (base.group === "your_move")
    return {
      ...base,
      clock: base.clock?.label === "Overdue" ? base.clock : agreedClock,
      committed: true,
      reminder: overdue,
    };
  return {
    group: "agreed",
    chips: base.chips,
    since,
    clock: agreedClock,
    promiseDue: null,
    committed: true,
    reminder: overdue,
  };
}

/** Call details ready to use, or changed after they were handled, are the rep's move. */
function phoneMove(
  phone: PhoneSummary | null | undefined,
  newest: Pick<InboxMessage, "at"> | null,
  clock: () => InboxClock | null,
): BasePosition | null {
  if (!phone) return null;
  if (!((phone.state === "ready" && !phone.handled) || phone.changedAfterHandling)) return null;
  return {
    group: "your_move",
    chips: [
      {
        label: phone.changedAfterHandling ? "Call details changed" : "Ready to call",
        tone: "blue",
      },
    ],
    since: newest?.at ?? null,
    clock: clock(),
    promiseDue: null,
  };
}

function operatorOutcomeLabel(kind: NonNullable<WorkPlan["operatorOutcome"]>["kind"]): string {
  return kind === "no_response" ? "Reviewed · no response" : "Reviewed · deck only";
}

type OwedPromise = WorkPlan["followUpPromises"][number];

/** The chip for one answer the rep owes now. To a lead who refused it names the debt, and no date. */
function owedChip(promise: OwedPromise, today: string, quiet: boolean): InboxChip {
  if (quiet) return { label: `Owes answer: ${promise.topic}`, tone: "slate", holdsRow: true };
  const late = today > promise.followUpOn;
  const suffix = late ? " · overdue" : today === promise.followUpOn ? " · due today" : "";
  return { label: `Owes answer: ${promise.topic}${suffix}`, tone: late ? "red" : "amber" };
}

/**
 * What the answers the rep owes now put on the row: a chip for each, the clock of the hardest date
 * among them, and whether the summary counts the row in "answers due".
 *
 * `quiet` is a lead whose last word is a no (`saidNo`). The promise is still the rep's own and still
 * holds the row in Your move, so the chip stays, but nothing counts down to it: no "Overdue 3 d" in
 * red, no "· overdue", no place in "answers overdue". Each of those asked the rep to act on a no,
 * which the app shows and never works (AGENTS.md, Boundaries).
 */
function owedAnswer(
  dueDebts: readonly OwedPromise[],
  today: string,
  quiet: boolean,
): { chips: InboxChip[]; clock: InboxClock | null; promiseDue: InboxPosition["promiseDue"] } {
  const chips = dueDebts.map((promise) => owedChip(promise, today, quiet));
  if (quiet) return { chips, clock: null, promiseDue: null };
  const overdue = dueDebts.filter((promise) => today > promise.followUpOn);
  const dueToday = dueDebts.filter((promise) => today === promise.followUpOn);
  const [firstOverdue] = [...overdue].sort((a, b) => a.followUpOn.localeCompare(b.followUpOn));
  const clock: InboxClock | null = firstOverdue
    ? { label: "Overdue", at: firstOverdue.followUpOn, tone: "alert" }
    : dueToday[0]
      ? { label: "Due today", at: dueToday[0].followUpOn, tone: "warn" }
      : null;
  return {
    chips,
    clock,
    promiseDue: overdue.length > 0 ? "overdue" : dueToday.length > 0 ? "today" : null,
  };
}

/**
 * The design's rules without agreement: closed, your move, the client's, then the lead's.
 *
 * For imported history, step 1 below (booked, call details, `handledReason`) has a twin in SQL:
 * `inbox-settled-history.ts` counts the rows those steps fold without running them, and
 * `tests/inbox-settled-history-postgres.test.ts` holds it to this function. A new way out of Handled
 * for imported history, in step 1 or ahead of it, must be named in that SQL rule too, or the lead
 * is counted and never shown.
 */
function whoseMove(entry: InboxEntry, now: Date): BasePosition {
  const { plan, messages } = entry;
  const lastInbound = lastOf(messages, "inbound");
  const after = (at: string | null): boolean =>
    at !== null && (lastInbound === null || ms(at) >= ms(lastInbound));
  const waiting = leadWaitingSince(messages);
  const newest = messages.at(-1) ?? null;
  // A settled row reads as a date; imported history reads as the date it happened (change 7).
  const lastClock = (): InboxClock | null => lastMessageClock(entry);
  const waitClock = (): InboxClock | null => {
    if (isUntouchedHistory(entry)) {
      const at = lastInbound ?? newest?.at ?? null;
      return at === null ? null : { label: "History", at, tone: "plain" };
    }
    if (waiting === null) return lastClock();
    const long = now.getTime() - ms(waiting) >= WAITING_LONG_MS;
    return { label: "Waiting", at: waiting, tone: long ? "warn" : "plain" };
  };
  const sentClock = (at: string | null): InboxClock | null => answerClock(entry, at);
  const closed = (chip: InboxChip): BasePosition => ({
    group: "closed",
    chips: [chip],
    since: newest?.at ?? null,
    clock: lastClock(),
    promiseDue: null,
  });

  // 1. Booked, then handled housekeeping, then the rest of closed.
  if (after(booking(plan))) return closed({ label: "Booked", tone: "green" });
  const phone = phoneMove(entry.phone, newest, waitClock);
  if (phone) return phone;
  const handled = handledReason(entry);
  if (handled !== null)
    return {
      group: "handled",
      chips: [{ label: handled, tone: entry.suppressed ? "red" : "slate" }],
      since: newest?.at ?? null,
      clock: lastClock(),
      promiseDue: null,
    };
  // A lead who opted out and then wrote back interested is shown, marked; drafting stays stopped.
  if (entry.suppressed && !isConverting(entry.classifications.at(-1)))
    return closed({ label: "Opted out", tone: "red" });
  if (plan.operatorOutcome && after(plan.operatorOutcome.reviewedAt))
    return closed({ label: operatorOutcomeLabel(plan.operatorOutcome.kind), tone: "slate" });
  if (plan.disposition === "ignored_not_qualified")
    return closed({ label: "Not qualified", tone: "slate" });
  if (plan.disposition === "sender_only_cc") return closed({ label: "Only Cc'd", tone: "slate" });

  /*
   * 2. Owed answers (change 4). A promise the client is checking is the client's move until its
   * follow-up date, and says who checks; on that date it is the rep's again, due, and after it,
   * overdue. An answer that is ready is the rep's to send whatever the date. The debt is never
   * invisible: it is either here, in Your move, or in Client's move with its date.
   */
  const today = calendarDay(now);
  const debts = plan.followUpPromises.filter((promise) => promise.state !== "kept");
  const dueDebts = debts.filter(
    (promise) => promise.state === "answer_ready" || today >= promise.followUpOn,
  );
  const checking = debts.filter(
    (promise) => promise.state === "open" && today < promise.followUpOn,
  );
  const checkingChips: InboxChip[] = checking.map((promise) => ({
    label: `${capitalized(promise.checker)} is checking ${promise.topic}`,
    tone: "slate",
  }));
  const work = plan.disposition === "active" ? openWork(plan) : [];

  // 3. Your move: an owed answer now due, held with the lead writing last, open rep work, or an
  // unanswered inbound.
  const leadWroteLast = newest?.direction === "inbound";
  if (dueDebts.length > 0) {
    const refused = saidNo(entry);
    const owed = owedAnswer(dueDebts, today, refused);
    return {
      group: "your_move",
      chips: [...work, ...owed.chips, ...checkingChips],
      since: dueDebts[0]?.authorizedAt ?? waiting,
      clock: refused ? lastClock() : (owed.clock ?? waitClock()),
      promiseDue: owed.promiseDue,
    };
  }
  /*
   * Drafting-off replies belong in neutral Held without a waiting clock: no model read means
   * neither Your move nor Closed/Handled is established. An already-classified automatic reply
   * still waits on the lead. See docs/2026-10-08-your-move-drafting-off-investigation.md.
   */
  const coveredAutoReply = (): boolean => {
    const latest = entry.classifications.at(-1);
    return (
      latest !== undefined &&
      autoReplyLabel(latest.stance) !== null &&
      newest !== null &&
      ms(newest.at) <= ms(latest.at)
    );
  };
  // The chip stays short: the list column truncated "Drafting off for this campaign" at 1,440 px,
  // and the open conversation names the level that holds it.
  if (plan.disposition === "drafting_off" && leadWroteLast && !coveredAutoReply())
    return {
      group: "held",
      chips: [{ label: "Drafting off", tone: "slate" }, ...checkingChips],
      since: newest?.at ?? null,
      clock: lastClock(),
      promiseDue: null,
    };
  if (isHeld(plan.disposition) && plan.disposition !== "drafting_off" && leadWroteLast)
    return {
      group: "your_move",
      chips: [
        {
          label: heldChipLabel({
            disposition: plan.disposition,
            draftingHeldBy: plan.draftingHeldBy,
          }),
          tone: "amber",
        },
        ...checkingChips,
      ],
      since: waiting,
      clock: waitClock(),
      promiseDue: null,
    };
  if (work.length > 0)
    return {
      group: "your_move",
      chips: [...work, ...checkingChips],
      since: waiting,
      clock: waitClock(),
      promiseDue: null,
    };
  if (checking.length > 0) {
    const [soonest] = [...checking].sort((a, b) => a.followUpOn.localeCompare(b.followUpOn));
    // To a lead who refused, the date an answer falls due is not a date the row counts down to.
    const dueClock: InboxClock | null = soonest
      ? { label: "Due", at: soonest.followUpOn, tone: "plain" }
      : null;
    return {
      group: "owed_by_client",
      chips: checkingChips,
      since: soonest?.authorizedAt ?? waiting,
      clock: saidNo(entry) ? lastClock() : dueClock,
      promiseDue: null,
    };
  }

  /*
   * 4. The lead's move: a seller reply closed this turn, we wrote last, reported sending, or Bison
   * shows the seller's answer. A recorded report reads from its record, keyed on the turn, so Bison's
   * copy arriving later cannot lift it the way it lifts a report keyed on the conversation revision.
   */
  const answered = entry.answeredTurn ?? null;
  if (answered?.reported)
    return {
      group: "waiting_on_lead",
      chips: [
        { label: answered.author === "rep" ? "You replied" : "Reported sent", tone: "slate" },
      ],
      since: answered.sentAt,
      clock: sentClock(answered.sentAt),
      promiseDue: null,
    };
  const sent = reportedAt(plan);
  if (sent !== null)
    return {
      group: "waiting_on_lead",
      chips: [{ label: "Reported sent", tone: "slate" }],
      since: sent,
      clock: sentClock(sent),
      promiseDue: null,
    };
  const lastOutbound = lastOf(messages, "outbound");
  if (plan.disposition === "answered_by_seller")
    return {
      group: "waiting_on_lead",
      chips: [{ label: "Answered in Bison", tone: "slate" }],
      since: lastOutbound ?? newest?.at ?? null,
      clock: sentClock(lastOutbound) ?? lastClock(),
      promiseDue: null,
    };
  const latestClassification = entry.classifications.at(-1);
  const autoLabel = latestClassification && autoReplyLabel(latestClassification.stance);
  if (
    autoLabel &&
    plan.steps.length === 0 &&
    leadWroteLast &&
    newest &&
    ms(newest.at) <= ms(latestClassification.at)
  )
    return {
      group: "waiting_on_lead",
      chips: [{ label: autoLabel, tone: "slate" }],
      since: lastOutbound ?? newest.at,
      clock: sentClock(lastOutbound) ?? lastClock(),
      promiseDue: null,
    };
  // A plan waiting on the clock (an out-of-office return, say) waits even when the lead wrote last.
  const wait = plan.steps.find((step) => step.input.kind === "wait" && step.state === "pending");
  if (!leadWroteLast || wait)
    return {
      group: "waiting_on_lead",
      chips: wait ? [{ label: "Wait step", tone: "slate" }] : [],
      since: lastOutbound ?? newest?.at ?? null,
      clock: sentClock(lastOutbound) ?? lastClock(),
      promiseDue: null,
    };

  /*
   * The rep answered and the app has produced nothing since: it is writing, or it stopped. Either
   * way the conversation is still the rep's, so it stays here, marked as not theirs to act on yet
   * (docs/2026-10-05-in-flight-visibility.md). Before this it was filed as done.
   */
  const flight = inFlightView(plan, entry.inFlight, now);
  if (flight)
    return {
      group: "your_move",
      chips: [IN_FLIGHT_CHIP[flight.kind]],
      since: waiting,
      clock: {
        label: "Since",
        at: flight.since,
        tone: flight.kind === "working" ? "plain" : "warn",
      },
      promiseDue: null,
      inFlight: flight,
    };
  // Every step finished and nothing sent since the lead wrote: the rep settled it without a reply.
  if (plan.steps.length > 0 && plan.steps.every((step) => FINISHED.has(step.state)))
    return closed({ label: "Done", tone: "green" });
  return {
    group: "your_move",
    chips: [
      plan.disposition === "active" && plan.steps.length === 0
        ? { label: "Drafting", tone: "slate" }
        : { label: "Reply needed", tone: "gold" },
    ],
    since: waiting,
    clock: waitClock(),
    promiseDue: null,
  };
}

export const inboxGroup = (entry: InboxEntry, now: Date): InboxGroup =>
  inboxPosition(entry, now).group;

/**
 * Show the lead's ask, then the rep's next step; stops take the second place unless holdsRow
 * explains work still owed to a refusing lead. Count hidden chips as +1 and name them in the
 * tooltip and accessible text, so reducing visible chips does not erase information.
 */
export function rowChips(chips: readonly InboxChip[]): { shown: InboxChip[]; hidden: InboxChip[] } {
  const [first, ...rest] = chips;
  if (first === undefined) return { shown: [], hidden: [] };
  const second =
    rest.find((chip) => chip.writing) ??
    rest.find((chip) => chip.holdsRow) ??
    (first.tone === "red" ? rest[0] : (rest.find((chip) => chip.tone === "red") ?? rest[0]));
  const shown = second === undefined ? [first] : [first, second];
  return { shown, hidden: rest.filter((chip) => chip !== second) };
}

/**
 * The tip on an "Unanswered 28 h" clock. The row says "Unanswered", not "Waiting": "Waiting on lead"
 * names the opposite case, where we wrote last (critique F2, 2026-10-18).
 */
export const unansweredTip = (when: string): string =>
  `Unanswered: the lead wrote last, on ${when}, and has had no reply yet`;

/** Waiting time as the row shows it: minutes, hours, then days. */
export function waitedLabel(since: string, now: Date): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - ms(since)) / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

/** The heading for a standing commitment: "Agreed, not booked", in the goal's own words. */
export function commitmentHeading(lifecycle: GoalLifecycle): string {
  const { commitment, completion } = lifecycle;
  if (commitment === null || completion.status !== "available") return "Commitment";
  return `${commitment}, ${completion.missing}`;
}

/** A lead waiting longer than a working day is shown in the warning tone. */
const WAITING_LONG_MS = 24 * 60 * 60 * 1000;

/*
 * The opening summary's buckets (change 1). Each is a filter that cuts across groups: a lead who
 * agreed may be in Your move or in Agreed, and the bucket shows both. Only one filter is ever on, so
 * choosing a bucket clears the group and choosing a group clears the bucket.
 *
 * Only what perishes is a bucket. "Need your answer" was one until the header was tightened on
 * 2026-09-21: its rows are always in Your move, each already carries its question as a chip, and in
 * the owner's screenshot it equalled the Your move tab. An old `?focus=answer` link opens the default
 * view. An answer that is due is never dropped: someone is waiting on a promise.
 */
const INBOX_FOCI = ["agreed", "due", "ready_to_call"] as const;
export type InboxFocus = (typeof INBOX_FOCI)[number];

export type InboxView = {
  group: InboxGroup | null;
  focus: InboxFocus | null;
  campaign: string | null;
  sort: InboxSort;
};
export const defaultInboxView: InboxView = {
  group: null,
  focus: null,
  campaign: null,
  sort: "action",
};

/*
 * The URL is an untrusted boundary: a hand-edited or stale link, including one from before the
 * grouping changed, falls back to the default for the parameter it got wrong.
 */
export function parseInboxView(search: string): InboxView {
  const params = new URLSearchParams(search);
  const group = z.enum(INBOX_GROUPS).safeParse(params.get("group"));
  const focus = z.enum(INBOX_FOCI).safeParse(params.get("focus"));
  const sort = z.enum(INBOX_SORTS).safeParse(params.get("sort"));
  const campaign = params.get("campaign");
  return {
    // A bucket in the link wins over a group in it: only one filter is ever on.
    group: group.success && !focus.success ? group.data : null,
    focus: focus.success ? focus.data : null,
    campaign: campaign ? campaign : null,
    sort: sort.success ? sort.data : "action",
  };
}

/*
 * What the inbox opens with. The rep opens the app to do work, so the default group is Your move
 * rather than every group at once; the group they last chose is remembered and wins over that.
 *
 * Pure on purpose: the caller reads the browser store and hands the value in, so the precedence is
 * testable without a browser and an unavailable store is just a null. A link that names a group
 * always wins, because a shared link must show the recipient what the sender saw. A link that names
 * none carries no opinion, so the reader's own remembered group applies.
 */
export const INBOX_GROUP_ALL = "all";

export function initialInboxView(search: string, remembered: string | null): InboxView {
  const fromUrl = parseInboxView(search);
  // A link naming a bucket shows exactly that bucket; a remembered group would narrow it unseen.
  if (fromUrl.focus !== null) return fromUrl;
  const named = new URLSearchParams(search).get("group");
  if (named !== null && z.enum(INBOX_GROUPS).safeParse(named).success) return fromUrl;
  if (named === INBOX_GROUP_ALL) return { ...fromUrl, group: null };
  if (remembered === INBOX_GROUP_ALL) return { ...fromUrl, group: null };
  const stored = z.enum(INBOX_GROUPS).safeParse(remembered);
  return { ...fromUrl, group: stored.success ? stored.data : "your_move" };
}

/** What to remember for the group the rep is looking at, including no group filter at all. */
export const rememberedGroupValue = (group: InboxGroup | null): string => group ?? INBOX_GROUP_ALL;

/** The query string for a view and selected conversation. Defaults are left out. */
export function inboxViewSearch(view: InboxView, planId: string | null): string {
  const params = new URLSearchParams();
  if (view.group) params.set("group", view.group);
  if (view.focus) params.set("focus", view.focus);
  if (view.campaign) params.set("campaign", view.campaign);
  if (view.sort !== "action") params.set("sort", view.sort);
  if (planId) params.set("plan", planId);
  const text = params.toString();
  return text ? `?${text}` : "";
}

/**
 * What a list view holds against what it shows, so every number the column prints can be added up
 * against the tabs. `loaded` is the view's rows in the browser's window (before the Handled fold),
 * `whole` the same view counted over the whole workspace, or null where only the window can be
 * counted: a search, a campaign filter or a summary bucket narrows the window, never the workspace.
 * `partial` says the window holds fewer conversations than the workspace.
 */
export type ViewCompleteness = {
  shown: number;
  folded: number;
  loaded: number;
  whole: number | null;
  partial: boolean;
};

/**
 * Explain when search/campaign/summary filters looked only at the loaded window; do not repeat
 * the header's truncation note. Return null when no distinct information remains.
 * beyondWindow includes extra Your move rows loaded for two tracks: count those rows and name
 * their source rather than claiming the filter searched only the 200 most recent.
 */
export function inboxCaption(view: ViewCompleteness, beyondWindow = 0): string | null {
  if (view.whole !== null || !view.partial) return null;
  const window = appCount(INBOX_WINDOW_ROWS);
  if (beyondWindow <= 0) return `Filters look only at the ${window} most recent conversations.`;
  const loaded = appCount(INBOX_WINDOW_ROWS + beyondWindow);
  return `Filters look only at the ${loaded} conversations loaded: the ${window} most recent and ${appCount(beyondWindow)} more from Your move.`;
}

/** Report rows cut or folded per view in the visible header; workspace-wide subtraction misstates tabs. */
export function hiddenRowsNote(
  view: Pick<ViewCompleteness, "folded" | "loaded" | "whole">,
): string | null {
  const older = view.whole === null ? 0 : view.whole - view.loaded;
  const notes = [
    older > 0 ? `${appCount(older)} older not loaded` : null,
    view.folded > 0 ? `${appCount(view.folded)} handled hidden below` : null,
  ].filter((note) => note !== null);
  return notes.length > 0 ? notes.join(" · ") : null;
}

/** Count per group across the conversations in scope, before the group filter itself applies. */
export function countByGroup<T extends InboxEntry>(
  entries: readonly T[],
  now: Date,
): Record<InboxGroup, number> {
  const counts: Record<InboxGroup, number> = {
    your_move: 0,
    agreed: 0,
    owed_by_client: 0,
    waiting_on_lead: 0,
    held: 0,
    closed: 0,
    handled: 0,
  };
  for (const entry of entries) counts[inboxGroup(entry, now)] += 1;
  return counts;
}

const newestAt = (entry: InboxEntry): number => {
  const at = entry.messages.at(-1)?.at;
  return at === undefined ? Number.NEGATIVE_INFINITY : ms(at);
};

/** Whether the newest classification shows engagement or booking intent: the ask is a time, a meeting or a registration. */
const showsIntent = (classifications: readonly InboxClassification[]): boolean => {
  const latest = classifications.at(-1);
  return (
    latest !== undefined &&
    (ENGAGED_STANCE_SET.has(latest.stance) || latest.asks.some((ask) => AGREED_ASK_SET.has(ask)))
  );
};

/** A calendar date after every real one, read by the same comparison the real ones are. */
const NO_DUE_DATE = "9999-12-31";

/*
 * The default ranks by group in the order above. Within your move, a lead whose newest reply shows
 * engagement or booking intent comes first, then the longest-waiting lead. Bison's Interested flag
 * no longer orders your move: it is set on a lead's first reply and never corrected, so it ranked a
 * once-curious thread above one asking for times today. It still orders the other groups, and stays
 * a chip and a filter; it changes order only, never membership (pilot plan). Within agreed, a row due its 7-day reminder comes
 * first, then the longest agreed. Client's move shows the soonest follow-up date first. Other groups
 * show their newest first. The chronological sorts are what they say.
 *
 * Imported history sorts after every live reply in Your move (change 7): its age is when it
 * happened, and read as a wait it put a months-old thread ahead of a lead who wrote this morning.
 * A reply the app held during campaign setup sorts after fresh ones of the same intent, for the
 * same reason.
 */
export function sortInbox<T extends InboxEntry>(
  entries: readonly T[],
  sort: InboxSort,
  now: Date,
): T[] {
  const positions = new Map(entries.map((entry) => [entry, inboxPosition(entry, now)]));
  const rank = (entry: T) => INBOX_GROUPS.indexOf(positions.get(entry)?.group ?? "closed");
  const since = (entry: T) => {
    const at = positions.get(entry)?.since;
    return at ? ms(at) : Number.POSITIVE_INFINITY;
  };
  // The date the client's answer is due. A row with no such date follows every row that has one.
  const dueOn = (entry: T) => {
    const clock = positions.get(entry)?.clock;
    return clock?.label === "Due" ? clock.at : NO_DUE_DATE;
  };
  return [...entries].sort((a, b) => {
    if (sort === "action") {
      const byGroup = rank(a) - rank(b);
      if (byGroup !== 0) return byGroup;
      const group = positions.get(a)?.group;
      if (group === "agreed") {
        const due = Number(positions.get(b)?.reminder) - Number(positions.get(a)?.reminder);
        if (due !== 0) return due;
        return since(a) - since(b);
      }
      if (group === "owed_by_client") {
        const byDate = dueOn(a).localeCompare(dueOn(b));
        if (byDate !== 0) return byDate;
      }
      if (group === "your_move") {
        const byHistory = Number(isUntouchedHistory(a)) - Number(isUntouchedHistory(b));
        if (byHistory !== 0) return byHistory;
        // The decay order: a standing commitment, then a lead asking for the next step, then the
        // longest wait. Bison's Interested no longer orders anything; it is a chip and a filter.
        const byCommitment =
          Number(positions.get(b)?.committed) - Number(positions.get(a)?.committed);
        if (byCommitment !== 0) return byCommitment;
        const byIntent =
          Number(showsIntent(b.classifications)) - Number(showsIntent(a.classifications));
        if (byIntent !== 0) return byIntent;
        // Within the same intent a fresh reply comes before one the app held during setup
        // (Dawid, 2026-09-29): the late one keeps its true age and is never shown as new, but its
        // long wait was the app's, so it does not jump the fresh replies it would otherwise lead.
        const byLate = Number(!!a.lateReply) - Number(!!b.lateReply);
        if (byLate !== 0) return byLate;
        const byWait = since(a) - since(b);
        if (byWait !== 0) return byWait;
      }
    }
    return newestAt(b) - newestAt(a);
  });
}

/** Whether a conversation belongs to a summary bucket. One definition for the count and the filter. */
export function inFocus(
  entry: InboxEntry,
  focus: InboxFocus,
  now: Date,
  position: InboxPosition = inboxPosition(entry, now),
): boolean {
  switch (focus) {
    case "ready_to_call":
      return (
        entry.phone?.state === "ready" &&
        (!entry.phone.handled ||
          entry.plan.steps.some(
            (step) =>
              step.input.kind === "reply" &&
              !["completed", "reported", "skipped"].includes(step.state),
          ))
      );
    case "agreed":
      return position.committed;
    case "due":
      return position.promiseDue !== null;
  }
}

/** Campaign filter, then the bucket or group filter, then sort: what the list shows. */
export function applyInboxView<T extends InboxEntry>(
  entries: readonly T[],
  view: InboxView,
  now: Date,
): T[] {
  const { focus, group } = view;
  return sortInbox(
    entries.filter(
      (entry) =>
        (view.campaign === null || entry.plan.campaignId === view.campaign) &&
        (focus !== null
          ? inFocus(entry, focus, now)
          : group === null || inboxGroup(entry, now) === group),
    ),
    view.sort,
    now,
  );
}

export type SummaryBucket = {
  focus: InboxFocus;
  count: number;
  label: string;
  tone: InboxChipTone;
};

/**
 * The opening summary (change 1): what perishes and how much, in decay order, at most two buckets
 * and none that is empty. The agreed bucket speaks in the goal's own words when every row in
 * it shares one goal, and in the generic "not completed" when goals are mixed, so a contribution
 * campaign is never told it has something "not booked".
 */
export function inboxSummary(entries: readonly InboxEntry[], now: Date): SummaryBucket[] {
  const buckets: SummaryBucket[] = [];
  const ready = entries.filter((entry) => inFocus(entry, "ready_to_call", now)).length;
  if (ready)
    buckets.push({
      focus: "ready_to_call",
      count: ready,
      label: `${ready} ready to call`,
      tone: "blue",
    });
  const agreed = entries.filter((entry) => inFocus(entry, "agreed", now));
  if (agreed.length > 0) {
    const missing = new Set(
      agreed.map((entry) => {
        const { completion } = goalLifecycle(entry.plan.goalKind);
        return completion.status === "available" ? completion.missing : "not completed";
      }),
    );
    const [only] = missing;
    buckets.push({
      focus: "agreed",
      count: agreed.length,
      label: `${agreed.length} agreed, ${missing.size === 1 && only ? only : "not completed"}`,
      tone: agreed.some((entry) => inboxPosition(entry, now).reminder) ? "amber" : "blue",
    });
  }
  const due = entries.map((entry) => inboxPosition(entry, now).promiseDue).filter(Boolean);
  if (due.length > 0) {
    const allOverdue = due.every((state) => state === "overdue");
    buckets.push({
      focus: "due",
      count: due.length,
      label: `${plural(due.length, "answer", "answers")} ${allOverdue ? "overdue" : "due"}`,
      tone: due.includes("overdue") ? "red" : "amber",
    });
  }
  return buckets;
}

/** Offer a bucket only if it changes the rows; always offer a pressed bucket so it can be cleared. */
export function summaryBucketNarrows<T extends InboxEntry>(
  entries: readonly T[],
  focus: InboxFocus,
  view: InboxView,
  now: Date,
): boolean {
  if (view.focus !== null) return true;
  const current = applyInboxView(entries, view, now);
  const bucket = entries.filter((entry) => inFocus(entry, focus, now));
  return !(bucket.length === current.length && bucket.every((entry) => current.includes(entry)));
}

/** What is narrowing the list, in the terms the rep set it: each one has its own way out. */
type InboxNarrowing =
  | { kind: "search"; text: string }
  | { kind: "campaign"; campaignId: string }
  | { kind: "group"; group: InboxGroup }
  | { kind: "focus"; focus: InboxFocus };

/** An owner that does hold rows, and how many, for an empty tab to point at. */
export type InboxElsewhere = { group: InboxGroup; count: number };

/**
 * Which of the three empty lists this is. `null` means rows are on screen and there is nothing to
 * explain.
 *
 * `partial` says the counts came from the inbox window rather than the workspace, so the copy can
 * stop short of calling them the whole. `matched` and `total` are the sets the rep can act on: the
 * rows the current search and campaign admit, and what the workspace holds.
 */
export type InboxEmptyState =
  | { kind: "workspace_empty" }
  | { kind: "filtered_empty"; narrowing: InboxNarrowing[]; total: number; partial: boolean }
  | {
      kind: "tab_empty";
      narrowing: InboxNarrowing[];
      elsewhere: InboxElsewhere[];
      matched: number;
      partial: boolean;
    };

/**
 * Why the list is empty, and where the rest of the work is.
 *
 * The inbox opens on Your move (`initialInboxView`), so a workspace whose rows are all history or
 * all waiting on the lead opens with an empty list. Until 2026-09-21 that state said "Nothing in
 * 'Your move' right now." and stopped there, and the owner read a workspace of 456 imported
 * conversations as holding none: the sentence is true about the tab and says nothing about the
 * workspace, and a tab is not what a person thinks they are looking at.
 *
 * The three states are distinguished here rather than in the component, because which one applies
 * is arithmetic over sets the caller already has, and because the difference between "you are
 * finished" and "nothing arrived" is the whole message.
 */
export function inboxEmptyState(input: {
  view: InboxView;
  search: string;
  total: number;
  matched: number;
  counts: Record<InboxGroup, number>;
  shown: number;
  folded: number;
  partial: boolean;
}): InboxEmptyState | null {
  const { view, total, matched, counts, shown, partial } = input;
  if (shown > 0) return null;
  // Nothing imported beats every filter: there is no narrowing to undo, and saying otherwise sends
  // the rep to clear a search that was never hiding anything.
  if (total === 0) return { kind: "workspace_empty" };

  const search = input.search.trim();
  const filters: InboxNarrowing[] = [
    search !== "" ? ({ kind: "search", text: search } as const) : null,
    view.campaign !== null ? ({ kind: "campaign", campaignId: view.campaign } as const) : null,
  ].filter((narrowing) => narrowing !== null);

  // Nothing survived the search or the campaign, so no tab holds anything either: offering one
  // would send the rep somewhere equally empty.
  if (matched === 0) return { kind: "filtered_empty", narrowing: filters, total, partial };

  const chosen: InboxNarrowing | null =
    view.focus !== null
      ? { kind: "focus", focus: view.focus }
      : view.group !== null
        ? { kind: "group", group: view.group }
        : null;
  return {
    kind: "tab_empty",
    narrowing: [...filters, ...(chosen ? [chosen] : [])],
    // In the tab order the header shows, and never the tab the rep is already reading.
    elsewhere: INBOX_GROUPS.filter(
      (group) => counts[group] > 0 && !(chosen?.kind === "group" && chosen.group === group),
    ).map((group) => ({ group, count: counts[group] })),
    matched,
    partial,
  };
}

/** What an empty list says: the state in a heading, and one line that adds to it or is absent. */
export type InboxEmptyCopy = { heading: string; detail: string | null };

const FOCUS_EMPTY: Record<InboxFocus, string> = {
  agreed: "Nothing agreed is still open",
  due: "No answers are due",
  ready_to_call: "No ready call details need handling or a reply",
};

/**
 * The words for an empty list.
 *
 * Three rules from the state it is given. An empty Your move is the rep's own queue being clear,
 * which is the state the product is trying to produce, so it reads as finished rather than as
 * broken. An empty workspace says only what the page can actually know — nothing has been imported
 * — and does not guess at intake being off, a cause the browser has no way to see. A filter that
 * matches nothing names the filter, because the way out is to undo it and the rep set it.
 *
 * The counts and the links live with the caller: this decides what is said, not what is clickable.
 */
export function inboxEmptyCopy(state: InboxEmptyState): InboxEmptyCopy {
  if (state.kind === "workspace_empty")
    return {
      heading: "No conversations yet",
      detail: "Replies appear here once this workspace imports them.",
    };
  if (state.kind === "filtered_empty") {
    const search = state.narrowing.find((one) => one.kind === "search");
    return {
      heading: search ? `Nothing matches “${search.text}”` : "Nothing matches these filters",
      detail:
        state.narrowing.length > 1
          ? "A search and a campaign filter are both on."
          : search
            ? null
            : "A campaign filter is on.",
    };
  }
  const chosen = state.narrowing.find((one) => one.kind === "focus" || one.kind === "group");
  const heading =
    chosen === undefined
      ? "Nothing here right now"
      : chosen.kind === "focus"
        ? FOCUS_EMPTY[chosen.focus]
        : chosen.group === "your_move"
          ? "Nothing needs you right now"
          : chosen.group === "held"
            ? "Nothing held right now"
            : `Nothing in ${inboxGroupLabel[chosen.group].toLowerCase()} right now`;
  // Held is the one group whose name does not say how a reply gets there.
  if (chosen?.kind === "group" && chosen.group === "held")
    return { heading, detail: "Replies wait here while their campaign's drafting is off." };
  return {
    heading,
    detail: state.elsewhere.length > 0 ? "The rest of this workspace is here:" : null,
  };
}

/**
 * The note under an empty list whose counts came from the window, or null. A search matching
 * nothing is the case that misleads: the conversation may exist and simply not be loaded, so the
 * note says what the search looked at rather than what was counted.
 */
export function emptyWindowNote(state: InboxEmptyState): string | null {
  if (state.kind === "workspace_empty" || !state.partial) return null;
  const searched = state.narrowing.some((one) => one.kind === "search");
  return searched
    ? `Search looks only at the ${appCount(INBOX_WINDOW_ROWS)} most recent conversations.`
    : `Counted over the ${appCount(INBOX_WINDOW_ROWS)} most recent conversations.`;
}

/** An owner as an empty state offers it: "63 waiting on lead". */
export const elsewhereLabel = (where: InboxElsewhere): string =>
  `${where.count} ${inboxGroupLabel[where.group].toLowerCase()}`;

const DAY_MS = 86_400_000;
/**
 * A calendar date or an instant as the row shows it: "24 Sep".
 *
 * Through `app-dates.ts`, which is the app's one date rule: it already reads a follow-up date as
 * the calendar day it is wherever the browser is, and it spells the month from a fixed table, so
 * this row reads the same word in every browser and on every runner.
 */
const dayMonth = (at: string, timeZone: string | undefined): string =>
  appDayMonth(at, timeZone === undefined ? {} : { timeZone });

/**
 * The row's clock as text, its word first: "Unanswered 5 h", "Agreed 9 d", "Due 24 Sep", "Overdue 2 d",
 * "Sent 30 h", "Last 12 Aug", "History 10 Aug". `timeZone` is for tests; the browser uses its own.
 */
export function clockText(clock: InboxClock | null, now: Date, timeZone?: string): string {
  if (clock === null) return "";
  switch (clock.label) {
    case "Waiting":
      return `Unanswered ${waitedLabel(clock.at, now)}`;
    case "Agreed":
    case "Sent":
      return `${clock.label} ${waitedLabel(clock.at, now)}`;
    case "Due":
      return `Due ${dayMonth(clock.at, timeZone)}`;
    case "Due today":
      return "Due today";
    case "Overdue": {
      const days = Math.round(
        (ms(`${calendarDay(now)}T00:00:00Z`) - ms(`${clock.at}T00:00:00Z`)) / DAY_MS,
      );
      return `Overdue ${days} d`;
    }
    case "Last":
    case "History":
      return `${clock.label} ${dayMonth(clock.at, timeZone)}`;
    case "Since":
    case "Asked": {
      // A time of day, with its day once it is no longer today: an age cannot be checked at a glance.
      const options = timeZone === undefined ? {} : { timeZone };
      const today = dayMonth(clock.at, timeZone) === dayMonth(now.toISOString(), timeZone);
      return `${clock.label} ${today ? appTime(clock.at, options) : appWeekdayTime(clock.at, options)}`;
    }
  }
}

/**
 * What a row's clock measures, in a sentence, for its tooltip. "Agreed 5 h" left the rep to guess
 * agreed to what (critique of 2026-09-22); the tooltip says it in the goal's own words.
 */
export function clockExplanation(
  clock: InboxClock,
  goalKind: WorkPlan["goalKind"],
  timeZone?: string,
): string {
  const when = appDateTime(clock.at, timeZone === undefined ? {} : { timeZone });
  const day = dayMonth(clock.at, timeZone);
  switch (clock.label) {
    case "Waiting":
      return unansweredTip(when);
    case "Agreed": {
      const { completion } = goalLifecycle(goalKind);
      const open = completion.status === "available" ? `, ${completion.missing} yet` : "";
      return `The lead agreed to the next step on ${when}${open}`;
    }
    case "Due":
      return `An answer is promised for ${day}; the client is checking until then`;
    case "Due today":
      return "An answer promised to the lead is due today";
    case "Overdue":
      return `An answer promised to the lead was due on ${day}`;
    case "Sent":
      return `Our last reply was sent on ${when}`;
    case "Last":
      return `Last message on ${when}`;
    case "History":
      return `Imported history; last message on ${when}`;
    case "Since":
      return `Drafting started on ${when}, when you answered. Nothing has been sent`;
    case "Asked":
      return `A new draft was asked for on ${when}. Nothing has been sent`;
  }
}

/**
 * Handled rows are folded out of the list until asked for: the Handled filter, a search, or the
 * rep opening the fold. The selected conversation always stays, so a link to one never goes blank.
 */
export function foldHandled<T extends InboxEntry>(
  rows: readonly T[],
  options: { view: InboxView; searching: boolean; open: boolean; selected: string | null },
  now: Date,
): { rows: T[]; folded: number; handled: number } {
  const handled = rows.filter((entry) => inboxGroup(entry, now) === "handled");
  if (options.view.group === "handled" || options.searching || options.open)
    return { rows: [...rows], folded: 0, handled: handled.length };
  const kept = rows.filter(
    (entry) => !handled.includes(entry) || entry.plan.id === options.selected,
  );
  return { rows: kept, folded: rows.length - kept.length, handled: handled.length };
}

/*
 * The draft's freshness line (section 3.2): which message the draft answers, and whether the lead
 * has written again since. `answersMessageId` comes from the `draft.generated` event; a draft
 * backfilled from before the event record has none, and then nothing is claimed.
 */
export type DraftFreshness =
  | { kind: "unknown" }
  | { kind: "current"; answers: { id: string; at: string } }
  | { kind: "stale"; answers: { id: string; at: string }; newer: { id: string; at: string } };

export function draftFreshness(
  answersMessageId: string | null,
  messages: readonly (InboxMessage & { id: string })[],
): DraftFreshness {
  const answered = messages.find((message) => message.id === answersMessageId);
  if (!answered) return { kind: "unknown" };
  const answers = { id: answered.id, at: answered.at };
  const newer = messages.findLast(
    (message) => message.direction === "inbound" && ms(message.at) > ms(answered.at),
  );
  return newer
    ? { kind: "stale", answers, newer: { id: newer.id, at: newer.at } }
    : { kind: "current", answers };
}

/** The draft a reply step shows was generated from this event, or edited from one that was. */
export function answersMessageIdFor(
  events: readonly { kind: string; detail: object }[],
  draftRevision: number,
): string | null {
  let answers: string | null = null;
  for (const event of events) {
    if (event.kind !== "draft.generated") continue;
    const detail = z
      .object({ draftRevision: z.number(), answersMessageId: z.string().nullable() })
      .safeParse(event.detail);
    if (detail.success && detail.data.draftRevision <= draftRevision)
      answers = detail.data.answersMessageId;
  }
  return answers;
}

/** Remaining means not finished: a reply the rep reported as sent is done. */
export function stepsRemainingLabel(plan: WorkPlan): string {
  const remaining = visibleSteps(plan).filter((step) => !FINISHED.has(step.state)).length;
  if (remaining === 0) return "Done";
  return `${remaining} ${remaining === 1 ? "step" : "steps"} remaining`;
}

/*
 * The one step whose action is shown as the primary (gold) button: the first approved connector
 * step waiting to execute, else the first reply or connector step the rep can approve now. Gold
 * marks the next thing to do, so a blocked or later step never competes with it.
 */
export function leadStepId(plan: WorkPlan): string | null {
  const steps = visibleSteps(plan);
  const executable = steps.find(
    (step) => step.input.kind === "connector" && step.state === "approved",
  );
  if (executable) return executable.id;
  const approvable = steps.find(
    (step) =>
      (step.input.kind === "reply" || step.input.kind === "connector") &&
      (step.state === "pending" || step.state === "invalidated") &&
      dependenciesSatisfied(plan, step),
  );
  return approvable?.id ?? null;
}
